import type {
  ApiOnboardingGraph,
  DataTokenType,
  PlaybookVersionNoGraph,
  TypedOnboardingData,
} from '@onefootprint/request-types/dashboard';
import { OUTPUT_DATA_NAMESPACE } from '../components/output-node/output-node.constants';

/**
 * Outputs are keyed by the namespace they're persisted under, which is also how a downstream rule
 * addresses them. `POST /onboardings` returns them without it, so that's how they're shown.
 */
export const outputKeyLabel = (key: string) => key.replace(`${OUTPUT_DATA_NAMESPACE}.`, '');

/** The namespaced key a form's un-namespaced data key is published under. */
export const outputKey = (dataKey: string) => `${OUTPUT_DATA_NAMESPACE}.${dataKey}`;

/**
 * One group per publisher, because an onboarding is served by exactly one of them: a version in a
 * weighted deployment, or an Output node on one branch of a graph. Undefined when any publisher's
 * contract couldn't be resolved: a set that isn't known can't be compared against, and reading it
 * as publishing nothing would report every key as dropped.
 */
const resolvedOutputs = (sources: { outputs?: TypedOnboardingData[] }[]) =>
  sources.every(source => source.outputs) ? sources.map(source => source.outputs ?? []) : undefined;

/**
 * For a graph in hand: an uncommitted draft, or the version an edit session started from. A graph
 * with no Output node publishes nothing, so `[]`.
 */
export const graphOutputs = (graph: ApiOnboardingGraph) =>
  resolvedOutputs(
    graph.nodes.flatMap(node => {
      const context = node.context.context;
      return context?.kind === 'output' ? [context] : [];
    }),
  );

/** For committed versions, which carry their outputs without the graph. */
export const versionOutputs = (versions: PlaybookVersionNoGraph[]) => resolvedOutputs(versions);

export type OutputChange =
  | { kind: 'removed'; key: string }
  | { kind: 'retyped'; key: string; from: DataTokenType[]; to: DataTokenType[] };

/**
 * How many publishers publish each key, and every type it can arrive as. Types are pooled loose
 * rather than kept per publisher, since a caller only cares what it might have to parse: one
 * expression resolving to `string` or `null` is the same contract as two versions, one of each.
 */
const contract = (publishers: TypedOnboardingData[][]) => {
  const keys = new Map<string, { publishers: number; types: Set<DataTokenType> }>();
  for (const outputs of publishers) {
    const counted = new Set<string>();
    for (const { key, dataType } of outputs) {
      const entry = keys.get(key) ?? { publishers: 0, types: new Set<DataTokenType>() };
      for (const type of dataType) {
        entry.types.add(type);
      }
      // One publisher spreading a key over two rows still publishes it once.
      if (!counted.has(key)) {
        entry.publishers += 1;
        counted.add(key);
      }
      keys.set(key, entry);
    }
  }
  return keys;
};

/**
 * The published outputs the pending set stops honoring. A key breaks by disappearing only if every
 * deployed publisher published it, since a caller already handles a conditional one being absent.
 * Types break by widening rather than by narrowing, on the same reasoning: a caller already handles
 * every type deployed today, so dropping one is safe and introducing one isn't. A key only the
 * pending set publishes is additive, so it's never reported.
 */
export const breakingOutputChanges = (
  deployed: TypedOnboardingData[][],
  pending: TypedOnboardingData[][],
): OutputChange[] => {
  const before = contract(deployed);
  const after = contract(pending);

  return [...before].flatMap(([key, was]): OutputChange[] => {
    const now = after.get(key);
    // One version dropping the key leaves the caller handling its absence, same as all of them
    // dropping it, so both report as removed.
    if (was.publishers === deployed.length && now?.publishers !== pending.length) {
      return [{ kind: 'removed', key }];
    }
    if (!now) {
      return [];
    }
    const hasNewType = [...now.types].some(type => !was.types.has(type));
    return hasNewType ? [{ kind: 'retyped', key, from: [...was.types], to: [...now.types] }] : [];
  });
};
